import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { apiGet, apiPost } from 'api';
import { baseUrl } from 'api/helpers';
import AuthContext from 'context/auth-context';

const getSqlSchemaCheck = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/sql_schema_check`;
  const data = await apiGet({ url, token });
  return data;
};

const runSqlSchemaCheck = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/run_sql_schema_check`;
  const data = await apiPost({ url, token, objectName: 'municipality', params: {} });
  return data;
};

const getSqlSchemaDriftEmail = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/sql_schema_drift_email`;
  const data = await apiGet({ url, token });
  return data;
};

const getSqlSchemaDriftScripts = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/sql_schema_drift_scripts`;
  const data = await apiGet({ url, token });
  return data;
};

// Downloads the drift scripts as a zip. The endpoint requires the bearer token,
// so we fetch the blob with auth rather than navigating to the URL, then trigger
// a client-side download.
const downloadSqlSchemaDriftScriptsZip = async (token, municipalityId, filename) => {
  const url = `${baseUrl}/admin/municipalities/${municipalityId}/sql_schema_drift_scripts?download=zip`;
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob'
  });

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename || 'drift_SQL.zip';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export function useAdminMunicipalitySqlSchema({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSqlSchemaCheck(token, municipalityId);
  const queryKey = ['admin-municipality-sql-schema', municipalityId];
  return useQuery(queryKey, queryFn, { enabled: !!municipalityId, ...options });
}

export function useRunAdminMunicipalitySqlSchema({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    () => runSqlSchemaCheck(token, municipalityId),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality-sql-schema', municipalityId]);
        queryClient.invalidateQueries(['admin-municipalities']);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}

export function useAdminMunicipalitySqlSchemaDriftEmail({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  return useMutation(() => getSqlSchemaDriftEmail(token, municipalityId), options);
}

export function useAdminMunicipalitySqlSchemaDriftScripts({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  return useMutation(() => getSqlSchemaDriftScripts(token, municipalityId), options);
}

export function useDownloadAdminMunicipalitySqlSchemaDriftScripts({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  return useMutation(
    (filename) => downloadSqlSchemaDriftScriptsZip(token, municipalityId, filename),
    options
  );
}
